import { useState, useEffect, useCallback, useRef } from 'react';
import {
  Modal,
  Stack,
  Group,
  Text,
  TextInput,
  Select,
  Button,
  Table,
  Checkbox,
  Avatar,
  Badge,
  Box,
  Collapse,
  Center,
  Loader,
  Progress,
  Switch,
  Tooltip,
  Pagination as MantinePagination,
} from '@mantine/core';
import { useDisclosure, useDebouncedValue } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import { Search, ChevronDown, ChevronUp, ArrowRight } from 'react-feather';
import type { CohortWithDetails, CoachStatus } from '../../../../types';
import { TimezoneSelect } from '../../../../components/common';
import {
  cohortCoachesApi,
  type AvailableCoachForPool,
} from '../../../../services/api/cohortCoaches';
import {
  CoachSatisfactionRating,
  CoachStatusPill,
  SortableTableHeader,
  type SortConfig,
} from '../../../../components/common';
import { getApiErrorMessage } from '../../../../services/api/client';
import { isMedleyEmail } from '../../../../utils/medleyEmail';
import {
  NO_MEDLEY_EMAIL_BADGE_STYLES,
  NO_MEDLEY_EMAIL_LABEL,
} from '../../../../components/coaches/noMedleyEmailBadge';

// MED-1355: the cohort pool needs no Medley email, so a CM can ask any coach
// for availability. Only coaches already in the cohort cannot be selected.
const canSelectCoach = (coach: AvailableCoachForPool): boolean => !coach.already_in_cohort;

// Faded cells on a row that cannot be selected.
const BLOCKED_OPACITY = 0.5;

// The local part keeps at least this width (in ch) with its ellipsis. In the
// 220px coach column, 5ch leaves room for a domain as long as
// "@digitalscientists.com" on its own line (measured in Overpass 12px).
const LOCAL_PART_MIN_CHARS = 5;

/**
 * MED-1355: an email with an optional label. The domain always shows,
 * because the domain tells a Medley email apart. The title shows the full
 * address on hover.
 *
 * - The address sits beside the label when it fits there in full. Else it
 *   goes to the next line, under the label, with the full cell width.
 * - In a narrow cell the local part shortens first, with an ellipsis. It
 *   keeps its first characters. Only when the cell is narrower than the
 *   start of the local part and the domain does the domain shorten too.
 *
 * Before, the label and the domain could not shrink, so a long personal
 * domain left the local part at 0 width. Now the local part starts at
 * 0 width and grows into the free space, up to its own width (flex 1 1 0,
 * max-width max-content). Its min-width keeps the first characters.
 */
function EmailText({ email, label }: { email: string; label?: string }) {
  const at = email.lastIndexOf('@');
  const local = at > 0 ? email.slice(0, at) : email;
  const domain = at > 0 ? email.slice(at) : '';
  const clip = { overflow: 'hidden', textOverflow: 'ellipsis' } as const;
  // A short local part shows in full and does not shrink.
  const localStyle =
    local.length <= LOCAL_PART_MIN_CHARS
      ? { flexShrink: 0 }
      : { ...clip, flex: '1 1 0', minWidth: `${LOCAL_PART_MIN_CHARS}ch`, maxWidth: 'max-content' };
  return (
    <Text
      size="xs"
      c="dimmed"
      title={label ? `${label}: ${email}` : email}
      style={{ display: 'flex', flexWrap: 'wrap', columnGap: 4, minWidth: 0, whiteSpace: 'nowrap' }}
    >
      {label && <span style={{ flexShrink: 0 }}>{label}:</span>}
      <span style={{ display: 'flex', flex: '1 1 auto', minWidth: 0, maxWidth: '100%' }}>
        <span style={localStyle}>{local}</span>
        <span style={{ ...clip, flex: '0 1 auto', minWidth: 0 }}>{domain}</span>
      </span>
    </Text>
  );
}

interface AddPotentialCoachesModalProps {
  opened: boolean;
  onClose: () => void;
  cohort: CohortWithDetails;
  onCoachesAdded: () => void;
}

// MED-683 AC7a: derived statuses only — Pending/Onboarding removed
const STATUS_OPTIONS = [
  { value: 'all', label: 'All Status' },
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive' },
];

const PAGE_SIZE = 25;

// Region options
const REGION_OPTIONS = [
  { value: 'all', label: 'All Regions' },
  { value: 'US - West', label: 'US - West' },
  { value: 'US - East', label: 'US - East' },
  { value: 'US - Central', label: 'US - Central' },
  { value: 'US - South', label: 'US - South' },
  { value: 'Europe', label: 'Europe' },
  { value: 'Asia', label: 'Asia' },
  { value: 'LATAM', label: 'LATAM' },
];

// "All Timezones" sits above the full IANA list so a CM can filter the coach
// pool by any zone a coach profile can now hold. The old 8-entry list had no
// India option at all.
const TIMEZONE_FILTER_EXTRAS = [{ value: 'all', label: 'All Timezones' }];

export function AddPotentialCoachesModal({
  opened,
  onClose,
  cohort,
  onCoachesAdded,
}: AddPotentialCoachesModalProps) {
  const [coaches, setCoaches] = useState<AvailableCoachForPool[]>([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [debouncedSearch] = useDebouncedValue(searchValue, 300);
  // MED-1355: default to All Status. The API adds an Inactive coach too,
  // and a new Medley coach is Inactive until a modality is approved. With an
  // Active default (MED-683 AC7), a CM could not find a coach they had just
  // created. The status pill still shows each coach's status.
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [regionFilter, setRegionFilter] = useState<string>('all');
  const [timezoneFilter, setTimezoneFilter] = useState<string>('all');
  const [programFilter, setProgramFilter] = useState<string>('all');
  const [showPartnerCoaches, setShowPartnerCoaches] = useState(false);
  const [selectedCoachIds, setSelectedCoachIds] = useState<string[]>([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [sortBy, setSortBy] = useState<string>('first_name');
  const [sortOrder, setSortOrder] = useState<'ASC' | 'DESC'>('ASC');
  const [moreFiltersOpened, { toggle: toggleMoreFilters }] = useDisclosure(false);
  const [programs, setPrograms] = useState<Array<{ value: string; label: string }>>([
    { value: 'all', label: 'All Programs' },
  ]);

  // Fetch available coaches. MED-1355: a search typed on page 2 starts one
  // request for page 2, then the reset to page 1 starts another. Only the
  // newest request may write the list, so a late stale answer cannot show
  // an empty list next to a total of 1.
  const latestRequestRef = useRef(0);
  const fetchCoaches = useCallback(async () => {
    const requestId = ++latestRequestRef.current;
    try {
      setLoading(true);
      const result = await cohortCoachesApi.getAvailablePool(cohort.id, {
        page,
        limit: PAGE_SIZE,
        search: debouncedSearch || undefined,
        status: statusFilter !== 'all' ? statusFilter : undefined,
        region: regionFilter !== 'all' ? regionFilter : undefined,
        timezone: timezoneFilter !== 'all' ? timezoneFilter : undefined,
        program_id: programFilter !== 'all' ? programFilter : undefined,
        coach_type: showPartnerCoaches ? 'partner' : 'medley',
        sortBy,
        sortOrder,
      });
      if (requestId !== latestRequestRef.current) return;
      setCoaches(result.data);
      setTotalPages(result.pagination?.totalPages || 1);
      setTotalCount(result.pagination?.total || 0);
    } catch (error) {
      if (requestId !== latestRequestRef.current) return;
      console.error('Failed to fetch coaches:', error);
    } finally {
      if (requestId === latestRequestRef.current) setLoading(false);
    }
  }, [cohort.id, page, debouncedSearch, statusFilter, regionFilter, timezoneFilter, programFilter, showPartnerCoaches, sortBy, sortOrder]);

  useEffect(() => {
    if (opened) {
      fetchCoaches();
    }
  }, [opened, fetchCoaches]);

  // Reset state when modal opens
  useEffect(() => {
    if (opened) {
      setSelectedCoachIds([]);
      setPage(1);
      setSearchValue('');
      setStatusFilter('all');
      setRegionFilter('all');
      setTimezoneFilter('all');
      setProgramFilter('all');
      setShowPartnerCoaches(false);
      setSortBy('first_name');
      setSortOrder('ASC');
    }
  }, [opened]);

  // MED-683 AC24: filter / search / sort changes reset to page 1
  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, statusFilter, sortBy, sortOrder]);

  // Fetch programs for filter dropdown
  useEffect(() => {
    const fetchPrograms = async () => {
      try {
        // Import programsApi dynamically to avoid circular dependencies
        const { programsApi } = await import('../../../../services/api/programs');
        const result = await programsApi.list({ limit: 100 });
        setPrograms([
          { value: 'all', label: 'All Programs' },
          ...result.data.map(p => ({ value: p.id, label: p.name })),
        ]);
      } catch (error) {
        console.error('Failed to fetch programs:', error);
      }
    };
    if (opened) {
      fetchPrograms();
    }
  }, [opened]);

  // Toggle coach selection (only if not already in cohort)
  const handleToggleCoach = (userId: string) => {
    const coach = coaches.find(c => c.user_id === userId);
    if (!coach || !canSelectCoach(coach)) return;

    setSelectedCoachIds(prev =>
      prev.includes(userId) ? prev.filter(id => id !== userId) : [...prev, userId],
    );
  };

  // Toggle all coaches on current page (excluding already added)
  const handleToggleAll = () => {
    const selectableIds = coaches.filter(canSelectCoach).map(c => c.user_id);
    const allSelected = selectableIds.every(id => selectedCoachIds.includes(id));
    if (allSelected) {
      setSelectedCoachIds(prev => prev.filter(id => !selectableIds.includes(id)));
    } else {
      setSelectedCoachIds(prev => [...new Set([...prev, ...selectableIds])]);
    }
  };

  // Submit selected coaches
  const handleSubmit = async () => {
    if (selectedCoachIds.length === 0) return;

    try {
      setSubmitting(true);
      await cohortCoachesApi.addToPool(cohort.id, selectedCoachIds);
      notifications.show({
        title: 'Coaches Added',
        message: `${selectedCoachIds.length} coach${selectedCoachIds.length !== 1 ? 'es' : ''} added to potential list`,
        color: 'green',
      });
      onCoachesAdded();
    } catch (error) {
      console.error('Failed to add coaches:', error);
      notifications.show({
        title: 'Error',
        message: getApiErrorMessage(error, 'Failed to add coaches to pool'),
        color: 'red',
      });
    } finally {
      setSubmitting(false);
    }
  };

  // Get coach full name
  const getCoachName = (coach: AvailableCoachForPool) => {
    const name = [coach.first_name, coach.last_name].filter(Boolean).join(' ');
    return name || coach.email || 'Unnamed coach';
  };

  // Get initials for avatar
  const getInitials = (coach: AvailableCoachForPool) => {
    if (coach.first_name && coach.last_name) {
      return `${coach.first_name[0]}${coach.last_name[0]}`.toUpperCase();
    }
    return (coach.email || '?')[0].toUpperCase();
  };

  // Sort handler
  const handleSort = useCallback((key: string) => {
    if (sortBy === key) {
      if (sortOrder === 'ASC') {
        setSortOrder('DESC');
      } else {
        setSortBy('first_name');
        setSortOrder('ASC');
      }
    } else {
      setSortBy(key);
      setSortOrder('ASC');
    }
    setPage(1);
  }, [sortBy, sortOrder]);

  const currentSortConfig: SortConfig | null = sortBy !== 'first_name' || sortOrder !== 'ASC'
    ? { key: sortBy, order: sortOrder }
    : null;

  // Render satisfaction with progress bar
  const renderSatisfactionCell = (coach: AvailableCoachForPool) => {
    if (coach.satisfaction_score === null || coach.satisfaction_score === undefined) {
      return <Text size="xs" c="dimmed">—</Text>;
    }
    const score = Number(coach.satisfaction_score);
    if (isNaN(score)) {
      return <Text size="xs" c="dimmed">—</Text>;
    }
    const percentage = (score / 5) * 100;
    const tierColor =
      score >= 4.5 ? '#28463A' :
      score >= 3.5 ? '#4A7C59' :
      score >= 2.5 ? '#E9A23B' :
      score >= 1.5 ? '#E07B4C' : '#D64545';
    return (
      <Group gap="xs" wrap="nowrap">
        <CoachSatisfactionRating
          rating={coach.satisfaction_score}
          surveyCount={coach.satisfaction_survey_count}
        />
        <Progress value={percentage} size="sm" w={60} color={tierColor} />
      </Group>
    );
  };

  const selectableCoaches = coaches.filter(canSelectCoach);
  const allCurrentPageSelected =
    selectableCoaches.length > 0 && selectableCoaches.every(c => selectedCoachIds.includes(c.user_id));
  const someCurrentPageSelected =
    selectableCoaches.some(c => selectedCoachIds.includes(c.user_id)) && !allCurrentPageSelected;

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={
        <Stack gap={4}>
          <Text fw={600} size="lg">Add Potential Coaches</Text>
          <Text size="sm" c="dimmed">{cohort.name} • {cohort.partner_name}</Text>
        </Stack>
      }
      size="xl"
      styles={{
        // MED-683 AC23: constrain modal height so the sticky footer always sits at the viewport bottom
        body: { padding: 0, display: 'flex', flexDirection: 'column', maxHeight: 'calc(85vh - 70px)' },
        content: { display: 'flex', flexDirection: 'column', maxHeight: '85vh' },
        header: { padding: '20px 24px', borderBottom: '1px solid var(--mantine-color-gray-2)' },
      }}
    >
      <Stack gap={0} style={{ flex: 1, minHeight: 0 }}>
        {/* Filters */}
        <Box p="md" style={{ borderBottom: '1px solid var(--mantine-color-gray-2)', flex: '0 0 auto' }}>
          <Group gap="md">
            <TextInput
              placeholder="Search by name or email..."
              leftSection={<Search size={16} />}
              value={searchValue}
              onChange={e => setSearchValue(e.target.value)}
              style={{ flex: 1 }}
            />
            <Select
              data={STATUS_OPTIONS}
              value={statusFilter}
              onChange={value => setStatusFilter(value || 'all')}
              w={150}
            />
            <Select
              data={[{ value: 'all', label: 'All Expertise' }]}
              defaultValue="all"
              w={150}
            />
            <Button
              variant="outline"
              rightSection={moreFiltersOpened ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
              onClick={toggleMoreFilters}
            >
              More Filters
            </Button>
            {selectedCoachIds.length > 0 && (
              <Badge size="lg" variant="light" color="medleyGreen">
                {selectedCoachIds.length} selected
              </Badge>
            )}
          </Group>

          {/* More Filters (collapsible) */}
          <Collapse in={moreFiltersOpened}>
            <Group gap="md" mt="md">
              <Select
                data={REGION_OPTIONS}
                value={regionFilter}
                onChange={value => {
                  setRegionFilter(value || 'all');
                  setPage(1);
                }}
                w={150}
              />
              <TimezoneSelect
                extraOptions={TIMEZONE_FILTER_EXTRAS}
                value={timezoneFilter}
                onChange={value => {
                  setTimezoneFilter(value || 'all');
                  setPage(1);
                }}
                w={180}
              />
              <Select
                data={programs}
                value={programFilter}
                onChange={value => {
                  setProgramFilter(value || 'all');
                  setPage(1);
                }}
                w={200}
                searchable
              />
              {/* MED-672 AC6a: hide partner toggle entirely when cohort has no partner */}
              {cohort.partner_id && (
                <Switch
                  label="Partner Coaches"
                  size="sm"
                  checked={showPartnerCoaches}
                  onChange={(e) => {
                    setShowPartnerCoaches(e.currentTarget.checked);
                    setPage(1);
                  }}
                />
              )}
            </Group>
          </Collapse>
        </Box>

        {/* Table — scrolls between filters and sticky footer */}
        <Box style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
          {loading ? (
            <Center py="xl">
              <Loader />
            </Center>
          ) : coaches.length === 0 ? (
            <Center py="xl">
              <Text c="dimmed">No coaches found matching your criteria</Text>
            </Center>
          ) : (
            <Table highlightOnHover>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th w={40}>
                    <Checkbox
                      checked={allCurrentPageSelected}
                      indeterminate={someCurrentPageSelected}
                      onChange={handleToggleAll}
                      disabled={selectableCoaches.length === 0}
                    />
                  </Table.Th>
                  {/* MED-683 AC20a: coach column ~30px narrower, content ellipsis-truncated */}
                  <Table.Th style={{ maxWidth: 220, width: 220 }}>
                    <SortableTableHeader label="COACH" sortKey="first_name" currentSort={currentSortConfig} onSort={handleSort} />
                  </Table.Th>
                  {/* MED-683 AC20: status column min 120px so the pill never truncates */}
                  <Table.Th style={{ minWidth: 120 }}>STATUS</Table.Th>
                  <Table.Th>
                    <SortableTableHeader label="REGION" sortKey="region" currentSort={currentSortConfig} onSort={handleSort} />
                  </Table.Th>
                  <Table.Th>
                    <SortableTableHeader label="TIMEZONE" sortKey="timezone" currentSort={currentSortConfig} onSort={handleSort} />
                  </Table.Th>
                  <Table.Th>EXPERTISE</Table.Th>
                  <Table.Th>
                    <SortableTableHeader label="SATISFACTION" sortKey="satisfaction" currentSort={currentSortConfig} onSort={handleSort} />
                  </Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {coaches.map(coach => {
                  const isAlreadyAdded = coach.already_in_cohort;
                  // MED-1355: a coach with no Medley email can join the pool.
                  // The badge tells the CM they need one before a group or a 1:1.
                  const needsMedleyEmail = !isMedleyEmail(coach.email);
                  const isBlocked = isAlreadyAdded;
                  const fade = { opacity: isBlocked ? BLOCKED_OPACITY : 1 };
                  return (
                    <Table.Tr
                      key={coach.user_id}
                      style={{
                        cursor: isBlocked ? 'not-allowed' : 'pointer',
                        backgroundColor: isBlocked ? 'var(--mantine-color-gray-0)' : undefined,
                      }}
                      onClick={() => !isBlocked && handleToggleCoach(coach.user_id)}
                    >
                      <Table.Td onClick={e => e.stopPropagation()} style={fade}>
                        <Checkbox
                          checked={selectedCoachIds.includes(coach.user_id)}
                          onChange={() => handleToggleCoach(coach.user_id)}
                          disabled={isBlocked}
                        />
                      </Table.Td>
                      <Table.Td style={{ maxWidth: 220 }}>
                        <Group gap="sm" wrap="nowrap" style={{ minWidth: 0 }}>
                          <Avatar
                            src={coach.avatar_url}
                            size="sm"
                            radius="xl"
                            color="medleyGreen"
                            style={fade}
                          >
                            {getInitials(coach)}
                          </Avatar>
                          <Stack gap={2} style={{ minWidth: 0, flex: 1 }}>
                            {/* MED-1355: the full name always shows. It wraps,
                                and the badges sit on their own line. */}
                            <Text size="sm" fw={500} style={{ overflowWrap: 'anywhere', ...fade }}>
                              {getCoachName(coach)}
                            </Text>
                            {(coach.coach_type === 'partner' || isAlreadyAdded || needsMedleyEmail) && (
                              <Group gap={4} style={fade}>
                                {coach.coach_type === 'partner' && (
                                  <Badge size="xs" variant="light" color="blue">
                                    Partner
                                  </Badge>
                                )}
                                {isAlreadyAdded && (
                                  <Badge size="xs" variant="light" color="gray">
                                    Already added
                                  </Badge>
                                )}
                                {needsMedleyEmail && (
                                  <Badge
                                    styles={NO_MEDLEY_EMAIL_BADGE_STYLES}
                                    title="Needs a Medley email before they can lead a group or be a 1:1 coach"
                                  >
                                    {NO_MEDLEY_EMAIL_LABEL}
                                  </Badge>
                                )}
                              </Group>
                            )}
                            {/* MED-1355 QA round 5: with no Medley email, the
                                personal email shows, labelled, so two coaches
                                with the same name can be told apart. An older
                                non-Medley users.email is labelled Sign-in, so
                                it does not read as the Medley email. */}
                            {coach.email && !needsMedleyEmail ? (
                              <Box style={fade}>
                                <EmailText email={coach.email} />
                              </Box>
                            ) : coach.preferred_email ? (
                              <Box style={fade}>
                                <EmailText email={coach.preferred_email} label="Personal" />
                              </Box>
                            ) : coach.email ? (
                              <Box style={fade}>
                                <EmailText email={coach.email} label="Sign-in" />
                              </Box>
                            ) : null}
                          </Stack>
                        </Group>
                      </Table.Td>
                      <Table.Td style={{ minWidth: 120, ...fade }}>
                        {/* MED-683 AC21/AC21a: tooltip explains derived status */}
                        <CoachStatusPill
                          status={coach.status as CoachStatus}
                          modalityApprovals={coach.modality_approvals ?? []}
                        />
                      </Table.Td>
                      <Table.Td style={fade}>
                        <Text size="sm">{coach.location || '-'}</Text>
                      </Table.Td>
                      <Table.Td style={fade}>
                        <Text size="sm">{coach.home_base_timezone || '-'}</Text>
                        {coach.active_cohort_count > 0 && (
                          <Text size="xs" c="dimmed">+{coach.active_cohort_count} more</Text>
                        )}
                      </Table.Td>
                      <Table.Td style={fade}>
                        {/* MED-683 AC22: hover tooltip lists ALL expertise items */}
                        <Tooltip
                          label={
                            coach.expertise_areas && coach.expertise_areas.length > 0
                              ? coach.expertise_areas.join(', ')
                              : 'No expertise listed'
                          }
                          multiline
                          w={280}
                          withArrow
                          disabled={!coach.expertise_areas || coach.expertise_areas.length === 0}
                        >
                          <Group gap={4} wrap="wrap" style={{ cursor: 'default' }}>
                            {coach.expertise_areas?.slice(0, 2).map(area => (
                              <Badge key={area} size="xs" variant="light" color="blue">
                                {area}
                              </Badge>
                            ))}
                            {(coach.expertise_areas?.length || 0) > 2 && (
                              <Text size="xs" c="dimmed">
                                +{(coach.expertise_areas?.length || 0) - 2}
                              </Text>
                            )}
                            {(!coach.expertise_areas || coach.expertise_areas.length === 0) && (
                              <Text size="xs" c="dimmed">—</Text>
                            )}
                          </Group>
                        </Tooltip>
                      </Table.Td>
                      <Table.Td style={fade}>
                        {renderSatisfactionCell(coach)}
                      </Table.Td>
                    </Table.Tr>
                  );
                })}
              </Table.Tbody>
            </Table>
          )}
        </Box>

        {/* MED-683 AC23: sticky footer pinned to bottom of modal viewport */}
        <Box
          p="md"
          style={{
            borderTop: '1px solid var(--mantine-color-gray-2)',
            backgroundColor: 'white',
            flex: '0 0 auto',
            position: 'sticky',
            bottom: 0,
          }}
        >
          <Group justify="space-between">
            <Text size="sm" c="dimmed">
              {totalCount === 0
                ? 'Showing 0 of 0 coaches'
                : `Showing ${(page - 1) * PAGE_SIZE + 1}-${Math.min(page * PAGE_SIZE, totalCount)} of ${totalCount} coaches`}
            </Text>
            <Group gap="md">
              {totalPages > 1 && (
                <MantinePagination value={page} total={totalPages} onChange={setPage} />
              )}
              <Group gap="xs">
                <Button variant="outline" onClick={onClose}>
                  Cancel
                </Button>
                <Button
                  color="medleyGreen"
                  onClick={handleSubmit}
                  disabled={selectedCoachIds.length === 0}
                  loading={submitting}
                  rightSection={<ArrowRight size={16} />}
                >
                  Add to Potential Coaches List
                </Button>
              </Group>
            </Group>
          </Group>
        </Box>
      </Stack>
    </Modal>
  );
}

export default AddPotentialCoachesModal;
